import React, { useMemo, useState, useEffect } from 'react';
import { Button, Card, CardBody, Container, Row, Col, Table } from 'reactstrap';
// Use styling-capable XLSX (xlsx-js-style) with fallback
let XLSX;
try { XLSX = require('xlsx-js-style'); } catch (e) { XLSX = require('xlsx'); }

// Month labels for display
const monthLabels = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

// Group API machine-wise data by oil_name for oil reports or by machine for water reports
const groupByOil = (apiData) => {
    // Normalise incoming structure
    const list = Array.isArray(apiData)
        ? apiData
        : Array.isArray(apiData?.data)
            ? apiData.data
            : Array.isArray(apiData?.payload)
                ? apiData.payload
                : [];
    if (!list.length) return [];
    const map = new Map();
    list.forEach(machine => {
        const key = machine.oil_name?.trim() || 'Unknown Oil';
        if (!map.has(key)) {
            map.set(key, {
                oil_name: key,
                perMonthQty: Array(12).fill(0),
                perMonthValue: Array(12).fill(0),
                total_qty: 0,
                total_value: 0
            });
        }
        const agg = map.get(key);
        machine.months?.forEach(m => {
            const idx = (m.month || 0) - 1;
            if (idx >= 0 && idx < 12) {
                agg.perMonthQty[idx] += Number(m.qty) || 0;
                agg.perMonthValue[idx] += Number(m.value) || 0;
            }
        });
        agg.total_qty += Number(machine.total_qty) || 0;
        agg.total_value += Number(machine.total_value) || 0;
    });
    return Array.from(map.values()).map((row, i) => ({ srno: i + 1, ...row }));
};

// Group API data for monthly coolant oil consumption report - shows oil name, machine name, and machine number
const groupByCoolantMachine = (apiData) => {
    console.log('groupByCoolantMachine - incoming apiData:', apiData);

    // Normalise incoming structure
    const list = Array.isArray(apiData)
        ? apiData
        : Array.isArray(apiData?.data)
            ? apiData.data
            : Array.isArray(apiData?.payload)
                ? apiData.payload
                : [];

    console.log('groupByCoolantMachine - extracted list:', list);

    if (!list.length) return [];

    // For monthly coolant oil consumption, each record should show oil name, machine name, and machine number
    const result = list.map((record, i) => {
        console.log('groupByCoolantMachine - processing record:', record);
        console.log('Available record fields:', Object.keys(record));
        return {
            srno: i + 1,
            oil_name: record.oil_name || 'Unknown Oil',
            machine_name: record.machine_name || record.name || 'Unknown Machine',
            machine_number: record.machine_number || record.machine_serial || record.serial_number || '',
            perMonthQty: record.months ? record.months.reduce((acc, m) => {
                const idx = (m.month || 0) - 1;
                if (idx >= 0 && idx < 12) {
                    acc[idx] = Number(m.qty) || 0;
                }
                return acc;
            }, Array(12).fill(0)) : Array(12).fill(0),
            perMonthValue: record.months ? record.months.reduce((acc, m) => {
                const idx = (m.month || 0) - 1;
                if (idx >= 0 && idx < 12) {
                    acc[idx] = Number(m.value) || 0;
                }
                return acc;
            }, Array(12).fill(0)) : Array(12).fill(0),
            total_qty: Number(record.total_qty) || 0,
            total_value: Number(record.total_value) || 0
        };
    });

    console.log('groupByCoolantMachine - final result:', result);
    return result;
};

// Group API machine-wise data by machine name and machine number for water consumption reports
const groupByMachine = (apiData) => {
    console.log('groupByMachine - incoming apiData:', apiData);

    // Normalise incoming structure
    const list = Array.isArray(apiData)
        ? apiData
        : Array.isArray(apiData?.data)
            ? apiData.data
            : Array.isArray(apiData?.payload)
                ? apiData.payload
                : [];

    console.log('groupByMachine - extracted list:', list);

    if (!list.length) return [];

    // For water consumption, each machine should be a separate row
    const result = list.map((machine, i) => {
        console.log('groupByMachine - processing machine:', machine);
        console.log('Available machine fields:', Object.keys(machine));
        return {
            srno: i + 1,
            machine_name: machine.machine_name || machine.name || 'Unknown Machine',
            machine_serial: machine.machine_number || '',
            perMonthQty: machine.months ? machine.months.reduce((acc, m) => {
                const idx = (m.month || 0) - 1;
                if (idx >= 0 && idx < 12) {
                    acc[idx] = Number(m.qty) || 0;
                }
                return acc;
            }, Array(12).fill(0)) : Array(12).fill(0),
            perMonthValue: machine.months ? machine.months.reduce((acc, m) => {
                const idx = (m.month || 0) - 1;
                if (idx >= 0 && idx < 12) {
                    acc[idx] = Number(m.value) || 0;
                }
                return acc;
            }, Array(12).fill(0)) : Array(12).fill(0),
            total_qty: Number(machine.total_qty) || 0,
            total_value: Number(machine.total_value) || 0
        };
    });

    console.log('groupByMachine - final result:', result);
    return result;
};

// companyName: optional simple string; if provided ensures a company row even when companyDetails is absent
const ReportYearlyWiseView = ({ onBack, yearlyData, reportYear, oilType, companyDetails, companyName, viewType }) => {
    const [rows, setRows] = useState([]);

    useEffect(() => {
        // Use different grouping based on report type
        console.log('ReportYearlyWiseView - yearlyData:', yearlyData);
        console.log('ReportYearlyWiseView - oilType:', oilType);
        console.log('ReportYearlyWiseView - viewType:', viewType);

        if (viewType === 'yearly_coolant') {
            // For monthly coolant oil consumption report, show oil name, machine name, and machine number
            const coolantRows = groupByCoolantMachine(yearlyData);
            console.log('ReportYearlyWiseView - coolantRows:', coolantRows);
            setRows(coolantRows);
        } else if (oilType === 'water') {
            const machineRows = groupByMachine(yearlyData);
            console.log('ReportYearlyWiseView - machineRows:', machineRows);
            setRows(machineRows);
        } else {
            setRows(groupByOil(yearlyData));
        }
    }, [yearlyData, oilType, viewType]);

    const filteredRows = rows; // (Search removed; all rows shown)

    // Totals per month across all oils
    const totalMonthQty = useMemo(() => {
        return monthLabels.map((_, i) => filteredRows.reduce((s, r) => s + (r.perMonthQty[i] || 0), 0));
    }, [filteredRows]);
    const totalMonthValue = useMemo(() => {
        return monthLabels.map((_, i) => filteredRows.reduce((s, r) => s + (r.perMonthValue[i] || 0), 0));
    }, [filteredRows]);

    const grandTotalQty = filteredRows.reduce((s, r) => s + (r.total_qty || 0), 0);
    const grandTotalValue = filteredRows.reduce((s, r) => s + (r.total_value || 0), 0);

    // Export current table view to Excel
    const handleExport = () => {
        const year = reportYear || new Date().getFullYear();
        const isWater = oilType === 'water';
        const isMonthlyCoolant = viewType === 'yearly_coolant';
        const title = `Monthly ${oilType ? (isWater ? 'Water' : (oilType.charAt(0).toUpperCase() + oilType.slice(1) + ' Oil')) : ''} Consumption Report`;
        const hasMachineCol = isWater; // For water, we show Machine Name and Serial Number
        const hasOilCol = !isWater && !isMonthlyCoolant; // For oil types (except monthly coolant), we show Oil Name
        const hasCoolantCols = isMonthlyCoolant; // For monthly coolant, we show Oil Name, Machine Name, and Machine Number
        // For water, remove Total Value column
        const showTotalValue = !isWater;
        const totalCols = (hasMachineCol ? 3 : (hasOilCol ? 2 : (hasCoolantCols ? 4 : 1))) + 12 + 1 + (showTotalValue ? 1 : 0); // SN + columns + 12 months + TotalQty + (Total Value if not water)
        const sheetData = [];

        // 1. Title row
        sheetData.push([title]); // row 0

        // 2. Company row (after title like daily report spec)
        // Try to infer company name if not explicitly passed
        let inferredCompany = '';
        if (!companyName && Array.isArray(yearlyData)) {
            const sample = yearlyData.find(o => o?.company_name || o?.companyName || o?.company);
            inferredCompany = sample?.company_name || sample?.companyName || sample?.company || '';
        }
        const resolvedCompanyName = (companyName || companyDetails?.name || companyDetails?.companyName || yearlyData?.company_name || yearlyData?.companyName || yearlyData?.data?.[0]?.company_name || inferredCompany || '').toString().trim();
        sheetData.push([`Company: ${resolvedCompanyName || '-'}`]); // row 1

        // 3. Optional additional company detail rows (single-cell merged style)
        if (companyDetails && typeof companyDetails === 'object') {
            const cd = companyDetails;
            const extraLine = (label, value) => {
                if (!value) return;
                sheetData.push([`${label}: ${value}`]);
            };
            extraLine('Address', cd.address);
            extraLine('Contact', cd.contact || cd.phone || cd.mobile);
            extraLine('Email', cd.email);
            extraLine('GST', cd.gst || cd.gstNumber);
            extraLine('PAN', cd.pan || cd.panNumber);
        }

        // 4. Blank spacer row
        sheetData.push(['']);

        // 5. Column header title row (merged previously style kept simple)
        const headers = ['S.no'];
        if (hasMachineCol) {
            headers.push('Machine Name', 'Machine Number');
        } else if (hasOilCol) {
            headers.push('Oil Name');
        } else if (hasCoolantCols) {
            headers.push('Oil Name', 'Machine Name', 'Machine Number');
        }
        headers.push(...monthLabels.map(m => `${m}-${year.toString().slice(-2)}`), 'TotalQty');
        if (showTotalValue) headers.push('Total Value');
        sheetData.push(headers);

        // Data rows
        filteredRows.forEach(r => {
            const base = [r.srno];
            if (hasMachineCol) {
                base.push(r.machine_name, r.machine_serial);
            } else if (hasOilCol) {
                base.push(r.oil_name);
            } else if (hasCoolantCols) {
                base.push(r.oil_name, r.machine_name, r.machine_number);
            }
            const rowArr = [
                ...base,
                ...r.perMonthQty.map(q => q ?? 0),
                r.total_qty ?? 0
            ];
            if (showTotalValue) rowArr.push(r.total_value ?? 0);
            sheetData.push(rowArr);
        });

        let totalQtyRowIndex = null;
        let totalValueRowIndex = null;
        if (filteredRows.length > 0) {
            totalQtyRowIndex = sheetData.length;
            const totalQtyRow = ['Total Qty'];
            if (hasMachineCol) {
                totalQtyRow.push('', ''); // Empty cells for Machine Name and Serial Number
            } else if (hasOilCol) {
                totalQtyRow.push(''); // Empty cell for Oil Name
            } else if (hasCoolantCols) {
                totalQtyRow.push('', '', ''); // Empty cells for Oil Name, Machine Name, Machine Number
            }
            totalQtyRow.push(...totalMonthQty.map(q => q ?? 0), grandTotalQty ?? 0);
            if (showTotalValue) totalQtyRow.push(0);
            sheetData.push(totalQtyRow);

            // Only add Total Value row if it's not a Monthly Water Consumption Report
            if (showTotalValue) {
                totalValueRowIndex = sheetData.length;
                const totalValueRow = ['Total Value'];
                if (hasMachineCol) {
                    totalValueRow.push('', ''); // Empty cells for Machine Name and Serial Number
                } else if (hasOilCol) {
                    totalValueRow.push(''); // Empty cell for Oil Name
                } else if (hasCoolantCols) {
                    totalValueRow.push('', '', ''); // Empty cells for Oil Name, Machine Name, Machine Number
                }
                totalValueRow.push(...totalMonthValue.map(v => v ?? 0), 0, grandTotalValue ?? 0);
                sheetData.push(totalValueRow);
            }
        }

        const ws = XLSX.utils.aoa_to_sheet(sheetData);
        // Determine how many single-cell merged rows before headers (title + company + extra details + spacer (not merged))
        const headerRowIndex = sheetData.findIndex(r => r[0] === 'S.no');
        const merges = [];
        // Merge all rows prior to spacer except the blank spacer row and the SN header row
        for (let r = 0; r < headerRowIndex; r++) {
            const firstCell = sheetData[r]?.[0];
            if (firstCell === undefined) continue;
            if (firstCell === '' && sheetData[r].length === 1) continue; // skip blank spacer
            // merge across totalCols
            merges.push({ s: { r, c: 0 }, e: { r, c: totalCols - 1 } });
        }
        // Merge total rows first columns if present
        const mergeColCount = hasMachineCol ? 3 : (hasOilCol ? 2 : (hasCoolantCols ? 4 : 1));
        if (mergeColCount > 1) {
            if (totalQtyRowIndex !== null) {
                merges.push({ s: { r: totalQtyRowIndex, c: 0 }, e: { r: totalQtyRowIndex, c: mergeColCount - 1 } });
            }
            if (totalValueRowIndex !== null) {
                merges.push({ s: { r: totalValueRowIndex, c: 0 }, e: { r: totalValueRowIndex, c: mergeColCount - 1 } });
            }
        }
        ws['!merges'] = merges;
        // Optional: set column widths for readability
        const columnWidths = [{ wch: 6 }]; // Srno
        if (hasMachineCol) {
            columnWidths.push({ wch: 28 }, { wch: 15 }); // Machine Name, Serial Number
        } else if (hasOilCol) {
            columnWidths.push({ wch: 28 }); // Oil Name
        } else if (hasCoolantCols) {
            columnWidths.push({ wch: 28 }, { wch: 28 }, { wch: 15 }); // Oil Name, Machine Name, Machine Number
        }
        columnWidths.push(...Array(12).fill({ wch: 10 }), { wch: 10 }); // Months, TotalQty
        if (showTotalValue) columnWidths.push({ wch: 12 }); // Total Value
        ws['!cols'] = columnWidths;

        const wb = XLSX.utils.book_new();
        // Apply requested styling for first two rows
        if (ws['A1']) ws['A1'].s = { font: { bold: false, sz: 12 }, alignment: { horizontal: 'center', vertical: 'center' } };
        if (ws['A2']) ws['A2'].s = { font: { bold: true, sz: 12 }, alignment: { horizontal: 'center', vertical: 'center' } };

        XLSX.utils.book_append_sheet(wb, ws, 'Yearly Report');
        const filename = `Monthly_${(isWater ? 'Water' : (oilType || 'Maintenance') + '_Oil')}_Consumption_Report_${year}.xlsx`.replace(/\s+/g, '_');
        XLSX.writeFile(wb, filename);
    };

    return (
        <Container fluid className='mt-4'>
            <Row>
                <Col xl='12'>
                    <Card className='shadow border-0'>
                        <CardBody className='bg-gray-50'>
                            <Row className='mb-3 align-items-center'>
                                <Col lg='6' md='12' className='mb-3 mb-lg-0'>
                                    <div style={{ display: "flex", alignItems: "center" }}>
                                        <Button
                                            color="link"
                                            onClick={onBack}
                                            className="p-0 text-xs no-underline"
                                        >
                                            <i className="fa fa-arrow-left mr-2"></i>
                                            Back
                                        </Button>
                                        <h6 className='mb-0'>Monthly {oilType ? (oilType === 'water' ? 'Water' : oilType.charAt(0).toUpperCase() + oilType.slice(1) + ' Oil') : ''} Consumption Report - {filteredRows.length} records</h6>
                                    </div>
                                </Col>
                                <Col lg='6' className='text-right text-muted small d-flex flex-column flex-lg-row justify-content-lg-end align-items-lg-center'>
                                    <div
                                        onClick={handleExport}
                                        disabled={rows.length === 0}
                                        style={{
                                            padding: '2px 4px',
                                            fontSize: '13px',
                                            fontWeight: '500',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            cursor: 'pointer',
                                            color: '#418efc',
                                            borderBottom: '1.5px solid #418efc',
                                        }}
                                    >
                                        <i className="fa-regular fa-circle-down" style={{ fontSize: '13px' }}></i>
                                        Download Excel
                                    </div>
                                </Col>
                            </Row>
                            <Row>
                                <Col xs='12'>
                                    <div className='overflow-x-auto' style={{ position: 'relative' }}>
                                        <Table className='table-hover table-flush table-bordered text-xs mb-0 bg-white' responsive style={{ minWidth: '1200px' }}>
                                            <thead className='thead-light'>
                                                <tr>
                                                    <th colSpan={(viewType === 'yearly_coolant' ? 4 : (oilType === 'water' ? 3 : 2)) + 12 + 2} className='text-center text-xs align-middle'>Monthly {oilType ? (oilType === 'water' ? 'Water' : oilType.charAt(0).toUpperCase() + oilType.slice(1) + ' Oil') : 'Maintenance'} Consumption Report</th>
                                                </tr>
                                                <tr>
                                                    <th className='text-center text-xs align-middle' style={{ position: 'sticky', left: '0', backgroundColor: '#f8f9fe', zIndex: 10, minWidth: '50px', borderRight: '2px solid #dee2e6' }}>SN</th>
                                                    {viewType === 'yearly_coolant' ? (
                                                        <>
                                                            <th className='text-left text-xs align-middle' style={{ position: 'sticky', left: '50px', backgroundColor: '#f8f9fe', zIndex: 10, minWidth: '160px', borderRight: '2px solid #dee2e6' }}>Oil Name</th>
                                                            <th className='text-left text-xs align-middle' style={{ position: 'sticky', left: '210px', backgroundColor: '#f8f9fe', zIndex: 10, minWidth: '130px', borderRight: '2px solid #dee2e6' }}>Machine Name</th>
                                                            <th className='text-left text-xs align-middle' style={{ position: 'sticky', left: '340px', backgroundColor: '#f8f9fe', zIndex: 10, minWidth: '120px', borderRight: '2px solid #dee2e6' }}>Machine Number</th>
                                                        </>
                                                    ) : oilType === 'water' ? (
                                                        <>
                                                            <th className='text-left text-xs align-middle' style={{ position: 'sticky', left: '50px', backgroundColor: '#f8f9fe', zIndex: 10, minWidth: '130px', borderRight: '2px solid #dee2e6' }}>Machine Name</th>
                                                            <th className='text-left text-xs align-middle' style={{ position: 'sticky', left: '180px', backgroundColor: '#f8f9fe', zIndex: 10, minWidth: '120px', borderRight: '2px solid #dee2e6' }}>Machine Number</th>
                                                        </>
                                                    ) : (
                                                        <th className='text-left text-xs align-middle' style={{ position: 'sticky', left: '50px', backgroundColor: '#f8f9fe', zIndex: 10, minWidth: '160px', borderRight: '2px solid #dee2e6' }}>Oil Name</th>
                                                    )}
                                                    {monthLabels.map((m, i) => <th key={m} className='text-center text-xs align-middle'>{m}-{(reportYear || new Date().getFullYear()).toString().slice(-2)}</th>)}
                                                    <th className='text-center text-xs align-middle'>TotalQty</th>
                                                    {oilType !== 'water' && (
                                                        <th className='text-center text-xs align-middle'>Total Value</th>
                                                    )}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredRows.length === 0 && (
                                                    <tr>
                                                        <td colSpan={(viewType === 'yearly_coolant' ? 4 : (oilType === 'water' ? 3 : 2)) + 12 + 2} className='text-center py-4 text-xs text-muted'>No records</td>
                                                    </tr>
                                                )}
                                                {filteredRows.map(row => (
                                                    <tr key={row.srno}>
                                                        <td className='text-center text-xs align-middle' style={{ position: 'sticky', left: '0', backgroundColor: 'white', zIndex: 9, minWidth: '50px', borderRight: '2px solid #dee2e6' }}>{row.srno}</td>
                                                        {viewType === 'yearly_coolant' ? (
                                                            <>
                                                                <td className='text-left text-xs align-middle' style={{ position: 'sticky', left: '50px', backgroundColor: 'white', zIndex: 9, minWidth: '160px', borderRight: '2px solid #dee2e6' }}>{row.oil_name}</td>
                                                                <td className='text-left text-xs align-middle' style={{ position: 'sticky', left: '210px', backgroundColor: 'white', zIndex: 9, minWidth: '130px', borderRight: '2px solid #dee2e6' }}>{row.machine_name}</td>
                                                                <td className='text-left text-xs align-middle' style={{ position: 'sticky', left: '340px', backgroundColor: 'white', zIndex: 9, minWidth: '120px', borderRight: '2px solid #dee2e6' }}>{row.machine_number}</td>
                                                            </>
                                                        ) : oilType === 'water' ? (
                                                            <>
                                                                <td className='text-left text-xs align-middle' style={{ position: 'sticky', left: '50px', backgroundColor: 'white', zIndex: 9, minWidth: '130px', borderRight: '2px solid #dee2e6' }}>{row.machine_name}</td>
                                                                <td className='text-left text-xs align-middle' style={{ position: 'sticky', left: '180px', backgroundColor: 'white', zIndex: 9, minWidth: '120px', borderRight: '2px solid #dee2e6' }}>{row.machine_serial}</td>
                                                            </>
                                                        ) : (
                                                            <td className='text-left text-xs align-middle' style={{ position: 'sticky', left: '50px', backgroundColor: 'white', zIndex: 9, minWidth: '160px', borderRight: '2px solid #dee2e6' }}>{row.oil_name}</td>
                                                        )}
                                                        {row.perMonthQty.map((q, idx) => (
                                                            <td key={idx} className='text-center text-xs align-middle'>{(q ?? 0).toFixed(2)}</td>
                                                        ))}
                                                        <td className='text-center text-xs align-middle'>{(row.total_qty ?? 0).toFixed(2)}</td>
                                                        {oilType !== 'water' && (
                                                            <td className='text-center text-xs align-middle'>{(row.total_value ?? 0).toFixed(2)}</td>
                                                        )}
                                                    </tr>
                                                ))}
                                                {/* Total Qty Row */}
                                                {filteredRows.length > 0 && (
                                                    <tr className='bg-gray-50'>
                                                        <td colSpan={viewType === 'yearly_coolant' ? 4 : (oilType === 'water' ? 3 : 2)} className='text-left text-xs align-middle' style={{ position: 'sticky', left: '0', backgroundColor: '#f8f9fe', zIndex: 9, minWidth: viewType === 'yearly_coolant' ? '460px' : (oilType === 'water' ? '300px' : '210px'), borderRight: '2px solid #dee2e6' }}>Total Qty</td>
                                                        {totalMonthQty.map((q, i) => (<td key={`tq-${i}`} className='text-center text-xs align-middle'>{(q ?? 0).toFixed(2)}</td>))}
                                                        <td className='text-center text-xs align-middle'>{(grandTotalQty ?? 0).toFixed(2)}</td>
                                                        {oilType !== 'water' && <td className='text-center text-xs align-middle'>0.00</td>}
                                                    </tr>
                                                )}
                                                {/* Total Value Row - Hide for Monthly Water Consumption Report */}
                                                {filteredRows.length > 0 && oilType !== 'water' && (
                                                    <tr className='bg-gray-50'>
                                                        <td colSpan={viewType === 'yearly_coolant' ? 4 : (oilType === 'water' ? 3 : 2)} className='text-left text-xs align-middle' style={{ position: 'sticky', left: '0', backgroundColor: '#f8f9fe', zIndex: 9, minWidth: viewType === 'yearly_coolant' ? '460px' : (oilType === 'water' ? '300px' : '210px'), borderRight: '2px solid #dee2e6' }}>Total Value</td>
                                                        {totalMonthValue.map((v, i) => (<td key={`tv-${i}`} className='text-center align-middle'>{(v ?? 0).toFixed(2)}</td>))}
                                                        <td className='text-center align-middle'>0.00</td>
                                                        <td className='text-center align-middle'>{(grandTotalValue ?? 0).toFixed(2)}</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </Table>
                                    </div>
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </Container >
    );
};

export default ReportYearlyWiseView;
